import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Calendar, Car, CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL ?? "";

async function publicApi(path, method = "GET", body = null) {
  const res = await fetch(`${BASE}/api${path}`, {
    method, headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => null);
  if (!res.ok) throw new Error(out?.detail || `HTTP ${res.status}`);
  return out;
}

export default function PublicBooking() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [showChange, setShowChange] = useState(false);
  const [newTime, setNewTime] = useState("");

  const load = useCallback(async () => {
    try {
      setB(await publicApi(`/public/bookings/${token}`));
    } catch (e) {
      setErr(e.message || "This booking link is invalid or has expired.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    setBusy(action);
    try {
      setB(await publicApi(`/public/bookings/${token}/${action}`, "POST"));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy("");
    }
  };

  const requestChange = async () => {
    if (!newTime) return;
    setBusy("change-request");
    try {
      const iso = new Date(newTime).toISOString();
      setB(await publicApi(`/public/bookings/${token}/change-request`, "POST", { requested_start_at: iso }));
      setShowChange(false); setNewTime("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy("");
    }
  };

  if (err) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-500" />
          <p className="mt-4 text-sm text-muted-foreground" data-testid="public-booking-error">{err}</p>
        </div>
      </div>
    );
  }
  if (!b) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  }

  const veh = [b.vehicle?.vrm, b.vehicle?.make, b.vehicle?.model].filter(Boolean).join(" ");
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10" data-testid="public-booking-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-lg">
        <p className="text-xs font-bold uppercase tracking-wider text-brand">{b.garage_name}</p>
        <h1 className="mt-1 text-xl font-bold">Your booking</h1>
        <span className="mt-2 inline-block rounded-full bg-muted px-3 py-1 text-xs font-semibold" data-testid="public-booking-status">{b.display_status}</span>

        <div className="mt-6 space-y-3 text-sm">
          <div className="flex items-center gap-3"><Car className="h-4 w-4 text-muted-foreground" /><span>{veh || "Your vehicle"}</span></div>
          <div className="flex items-center gap-3"><Calendar className="h-4 w-4 text-muted-foreground" /><span>{b.start_local || "—"}</span></div>
          {b.reason && <div className="flex items-center gap-3"><Clock className="h-4 w-4 text-muted-foreground" /><span>{b.reason}</span></div>}
        </div>

        {b.quote_status === "proposed" && (
          <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="public-quote-card">
            <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Quote from the garage</p>
            <p className="mt-1 text-2xl font-extrabold text-midnight" data-testid="public-quote-amount">£{Number(b.quoted_price).toFixed(2)}</p>
            {b.quote_note && <p className="mt-1 text-sm text-slate-600">{b.quote_note}</p>}
            <p className="mt-1 text-[11px] text-slate-500">No payment is taken online. Accepting agrees this price for the work described.</p>
            <div className="mt-3 flex gap-2">
              <button onClick={() => act("quote/accept")} disabled={!!busy} data-testid="public-quote-accept"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                {busy === "quote/accept" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Accept quote
              </button>
              <button onClick={() => act("quote/decline")} disabled={!!busy} data-testid="public-quote-decline"
                className="inline-flex flex-1 items-center justify-center rounded-lg border border-border px-4 py-2.5 text-sm font-semibold disabled:opacity-60">Decline</button>
            </div>
          </div>
        )}
        {b.quote_status === "accepted" && <p className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="public-quote-accepted"><CheckCircle2 className="h-4 w-4" />You accepted the quote of £{Number(b.quoted_price).toFixed(2)}.</p>}
        {b.quote_status === "declined" && <p className="mt-4 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600" data-testid="public-quote-declined">You declined this quote. The garage may contact you to discuss.</p>}
        {b.quote_status === "required" && b.price_display && <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">{b.price_display}</p>}

        {b.customer_confirmed && (
          <p className="mt-5 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> You confirmed this booking.
          </p>
        )}
        {b.cancellation_requested && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">Cancellation requested — the garage will be in touch.</p>
        )}
        {b.change_requested && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="public-change-requested">
            Change requested{b.requested_start_local ? ` to ${b.requested_start_local}` : ""} — awaiting the garage&apos;s confirmation. Your original time stays booked until they accept.
          </p>
        )}

        <div className="mt-6 flex flex-col gap-3">
          {b.can_confirm && (
            <button onClick={() => act("confirm")} disabled={!!busy} data-testid="public-confirm-btn"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {busy === "confirm" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Confirm booking
            </button>
          )}
          {b.can_request_change && !showChange && (
            <button onClick={() => setShowChange(true)} disabled={!!busy} data-testid="public-change-btn"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
              <Clock className="h-4 w-4" />Request a different time
            </button>
          )}
          {showChange && (
            <div className="rounded-xl border border-border bg-muted/40 p-3" data-testid="public-change-form">
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">Preferred date &amp; time</label>
              <input type="datetime-local" value={newTime} onChange={(e) => setNewTime(e.target.value)} data-testid="public-change-input"
                className="w-full rounded-lg border border-input px-3 py-2 text-sm" />
              <div className="mt-2 flex gap-2">
                <button onClick={requestChange} disabled={!!busy || !newTime} data-testid="public-change-submit"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                  {busy === "change-request" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Send request
                </button>
                <button onClick={() => { setShowChange(false); setNewTime(""); }} className="rounded-lg border border-border px-4 py-2.5 text-sm font-semibold">Cancel</button>
              </div>
            </div>
          )}
          {b.can_request_cancel && (
            <button onClick={() => act("cancel-request")} disabled={!!busy} data-testid="public-cancel-btn"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
              {busy === "cancel-request" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Request cancellation
            </button>
          )}
        </div>

        {b.garage_phone && <p className="mt-6 text-center text-xs text-muted-foreground">Need help? Call {b.garage_phone}</p>}
      </div>
    </div>
  );
}
